import { matchPath } from 'react-router-dom';
import { ROUTES } from 'constants/routes';

export type BhriguContextLayer = 'lesson' | 'batch' | 'school' | 'global';

export type BhriguContextStack = {
  layer: BhriguContextLayer;
  lessonId?: string;
  courseId?: string | null;
  schoolId?: string;
  schoolSlug?: string;
  batchId?: string;
  institutionId?: string;
  enrollmentId?: string;
};

const LEARN_ROUTE_SCHOOL: { prefix: string; slug: string }[] = [
  { prefix: ROUTES.PARASHARI_LEARN, slug: 'parashari' },
  { prefix: ROUTES.JAIMINI_LEARN, slug: 'jaimini' },
  { prefix: ROUTES.BNN_LEARN, slug: 'bnn' },
  { prefix: ROUTES.KP_LEARN, slug: 'kp' },
  { prefix: ROUTES.LAL_KITAB_LEARN, slug: 'lal-kitab' },
  { prefix: ROUTES.SWARA_VIGYAN_LEARN, slug: 'swara-vigyan' },
  { prefix: ROUTES.SURYA_SIDDHANTA_LEARN, slug: 'surya-siddhanta' },
];

function schoolSlugFromPath(pathname: string): string | null {
  for (const { prefix, slug } of LEARN_ROUTE_SCHOOL) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) return slug;
  }
  const schoolDetail = matchPath({ path: ROUTES.SCHOOL_DETAIL, end: false }, pathname);
  if (schoolDetail?.params.slug) return schoolDetail.params.slug;
  return null;
}

function lessonIdFromSchoolLearnSearch(schoolSlug: string, search: string): string | null {
  if (!search) return null;
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  if (schoolSlug === 'parashari') {
    return params.get('jyotishaTopic')?.trim() || null;
  }
  return params.get('topic')?.trim() || null;
}

/** Resolve static context from the current URL (enrollment fields merged in the hook). */
export function resolveBhriguContextFromPath(
  pathname: string,
  search = ''
): {
  stack: BhriguContextStack;
  pageKey: string | null;
  pageTitle: string;
} {
  const pathMatch = matchPath({ path: ROUTES.BATCH_PATH_TOPIC, end: true }, pathname);
  if (pathMatch?.params.batchId && pathMatch.params.topicId) {
    return {
      stack: {
        layer: 'lesson',
        batchId: pathMatch.params.batchId,
        lessonId: pathMatch.params.topicId,
      },
      pageKey: `batch:${pathMatch.params.batchId}:path:${pathMatch.params.topicId}`,
      pageTitle: 'Guru Path topic',
    };
  }

  const batchMatch = matchPath({ path: ROUTES.BATCH_DETAIL, end: false }, pathname);
  if (batchMatch?.params.batchId) {
    return {
      stack: {
        layer: 'batch',
        batchId: batchMatch.params.batchId,
      },
      pageKey: `batch:${batchMatch.params.batchId}`,
      pageTitle: 'My batch',
    };
  }

  const lessonMatch = matchPath({ path: ROUTES.LESSON, end: false }, pathname);
  if (lessonMatch?.params.lessonId) {
    return {
      stack: {
        layer: 'lesson',
        lessonId: lessonMatch.params.lessonId,
      },
      pageKey: `lesson:${lessonMatch.params.lessonId}`,
      pageTitle: 'Lesson',
    };
  }

  const courseMatch = matchPath({ path: ROUTES.COURSE_DETAIL, end: false }, pathname);
  if (courseMatch?.params.courseId) {
    return {
      stack: {
        layer: 'lesson',
        courseId: courseMatch.params.courseId,
      },
      pageKey: `course:${courseMatch.params.courseId}`,
      pageTitle: 'Course',
    };
  }

  const schoolSlug = schoolSlugFromPath(pathname);
  if (schoolSlug) {
    const topicId = lessonIdFromSchoolLearnSearch(schoolSlug, search);
    if (topicId) {
      return {
        stack: {
          layer: 'lesson',
          lessonId: topicId,
          schoolSlug,
        },
        pageKey: `school:${schoolSlug}:topic:${topicId}`,
        pageTitle: `${schoolSlug} topic`,
      };
    }
    return {
      stack: {
        layer: 'school',
        schoolSlug,
      },
      pageKey: `school:${schoolSlug}`,
      pageTitle: `${schoolSlug} school`,
    };
  }

  if (pathname === ROUTES.BATCHES) {
    return { stack: { layer: 'global' }, pageKey: 'app:batches', pageTitle: 'My batches' };
  }
  if (pathname === ROUTES.DASHBOARD) {
    return { stack: { layer: 'global' }, pageKey: 'app:dashboard', pageTitle: 'Dashboard' };
  }

  const segment = pathname.split('/').filter(Boolean)[0] ?? 'home';
  return {
    stack: { layer: 'global' },
    pageKey: `app:${segment}`,
    pageTitle: segment.replace(/-/g, ' '),
  };
}

/** Parashari learn shell only - uses PageFeedbackBubble with lesson page context. */
export function isParashariLearnPath(pathname: string): boolean {
  return pathname === ROUTES.PARASHARI_LEARN || pathname.startsWith(`${ROUTES.PARASHARI_LEARN}/`);
}

export { isSchoolLearnPath } from 'utils/learnerNavActive';
